'use client';
import type { ArbitraryTypedObject } from '@portabletext/types';
import Image from 'next/image';
import { PortableTextBlock } from '@sanity/types';
import React, { useState } from 'react';
import { getSanityImageUrl } from '@/libs/sanity/client';
import { PortableTextWrapper } from '@/components/PortableTextWrapper';
import { ChevronsRight } from 'lucide-react';
import { TABLET_SCREEN_SIZE } from '@/utils/constants';
import { useMediaQuery } from 'usehooks-ts';
import {
  Accordion,
  AccordionContainer,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  AccordionWrapper,
} from '@/components/ui/accordion';

type CardSlide2PartsBottomImageProps = {
  cardSlides: SlideItem[];
};

export type SlideItem = {
  id: number;
  title: string;
  content:
    | (PortableTextBlock | ArbitraryTypedObject)
    | (PortableTextBlock | ArbitraryTypedObject)[];
  icon?: any;
  bottomImage?: any;
};

const CardSlide2PartsBottomImage = ({
  cardSlides,
}: CardSlide2PartsBottomImageProps) => {
  const [selectedSlide, setSelectedSlide] = useState<SlideItem>(cardSlides[0]);
  const isTablet = useMediaQuery(`(max-width: ${TABLET_SCREEN_SIZE}px)`);
  const handleSlideClick = (slide: SlideItem) => setSelectedSlide(slide);

  const AccordionCard = () => (
    <AccordionContainer className="grid-cols-1">
      <AccordionWrapper>
        <Accordion>
          {cardSlides.map((item, index) => (
            <AccordionItem key={item.id} value={item.id.toString()}>
              <AccordionHeader leftIcon={true}>
                <div className="flex cursor-pointer items-center  flex-row justify-between w-full gap-x-2 lg:gap-x-3">
                  {index + 1}. {item.title}
                </div>
              </AccordionHeader>
              <AccordionPanel>
                <PortableTextWrapper value={item.content} />
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </AccordionWrapper>
    </AccordionContainer>
  );

  const CardSlide2PartsBottomImageSidebar = () => (
    <div className="flow-root">
      <ul role="list" className="-mb-12">
        {cardSlides.map((item, itemIdx) => (
          <li key={item.id}>
            <div className="relative pb-12">
              {itemIdx !== cardSlides.length - 1 && (
                <span
                  className="absolute left-[7px] top-1 -ml-px h-full w-0.5 bg-gray-300"
                  aria-hidden="true"
                />
              )}
              <div
                className="relative flex cursor-pointer flex-row justify-center w-full gap-x-2 lg:gap-x-3"
                onClick={() => handleSlideClick(item)}
              >
                <span
                  className={`h-[14px] w-[16px] rounded-full ring-1 ${item.id === selectedSlide.id ? 'bg-black ring-black' : 'bg-white ring-gray-300'}`}
                />
                <div className="flex items-center cursor-pointer flex-row justify-between w-full gap-x-2 lg:gap-x-3">
                  <span
                    className={`leading-[14px] text-sm drop-shadow-sm ${item.id === selectedSlide.id ? 'text-black' : 'text-gray-500'}`}
                  >
                    {item.title}
                  </span>
                  <ChevronsRight
                    className={`text-lg ${item.id === selectedSlide.id ? 'text-black' : 'text-gray-300'}`}
                    size="20"
                  />
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <section className="mt-[-20px]">
      <p className="text-lg drop-shadow-sm pb-6 font-medium">
        Click below to find out about the{' '}
        <span className="text-primary font-semibold">
          9 common and recurring challenges
        </span>
        <br />
        that large real estate players face:
      </p>
      {isTablet ? (
        <AccordionCard />
      ) : (
        <section className="grid grid-cols-1 md:grid-cols-12">
          <div className="col-span-1 md:col-span-4 border border-gray-50 bg-lightGray flex flex-col lg:flex-row pl-4 lg:pl-0 justify-start lg:justify-center py-12">
            <CardSlide2PartsBottomImageSidebar />
            {selectedSlide.icon && (
              <div className="justify-self-center self-end">
                <Image
                  src={getSanityImageUrl(selectedSlide.icon)}
                  alt={selectedSlide.title}
                  className="h-[102px] w-[102px] "
                  width={200}
                  height={200}
                  loading="lazy"
                />
              </div>
            )}
          </div>
          <div className="col-span-1 md:col-span-8 border border-gray-100 shadow-lg px-4 md:px-8 lg:px-12 pt-8 pb-8 md:pb-0">
            <h5 className="text-xl leading-tight pb-8">
              {selectedSlide.title}
            </h5>
            {selectedSlide.content && (
              <PortableTextWrapper value={selectedSlide.content} />
            )}
            {selectedSlide.bottomImage && (
              <div className="pb-12">
                <Image
                  src={getSanityImageUrl(selectedSlide.bottomImage)}
                  alt={selectedSlide.title}
                  className=""
                  width={1000}
                  height={500}
                  loading="lazy"
                />
              </div>
            )}
          </div>
        </section>
      )}
    </section>
  );
};

export default CardSlide2PartsBottomImage;
